import { FC, Fragment, useEffect, useRef, useState } from "react";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { getMySchedules, IMySchedule, IMyScheduleList } from "../api/getMySchedules";
import CardAttendanceLoader from "../components/loaders/CardAttendanceLoader";
import CardMySchedules from "../components/cards/CardMySchedules/CardMySchedules";
import SearchField from "../components/fields/SearchField";
import LazyLoad from "../components/LazyLoad";
import { SubTitle, Title } from "../components/Titles";
import CancelSchedulingModal from "../modals/CancelSchedulingModal";
import PageWrapper from "../components/common/PageWrapper";
import QuickSearch from "../components/QuickSearch";
import ButtonSecondary from "../components/buttons/ButtonSecondary";
import { FilterIcon } from "../components/Icons";
import FilterMySchedulesModal from "../modals/FilterMySchedulesModal";
import { IScheduleFilters, StatusType } from "../common/interfaces/ScheduleFilters";
import objectHaveOnlyUndefinedProperties from "../utils/objectHaveOnlyUndefinedProperties";
import ScheduleFilters from "../components/ScheduleFilters";

const Wrapper = styled.div`
	height: 100vh;
	display: flex;
	flex-direction: column;
	min-height: 0;
`;

const ContentArea = styled.div`
	padding: 0 25px;
`;

const MySchedulesWrapper = styled.div`
	height: 100vh;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 16px;
`;

const FilterWrapper = styled.div`
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	padding: 0 25px;
	margin-top: 25px;
	width: 100%;
	box-sizing: border-box;
	gap: 8px;
	& > button {
		flex-shrink: 0;
		color: ${props => (props.theme.colors.blackVitat)} !important;
		background-color: ${props => (props.theme.colors.lightGrayVitat)
	}
`;

const ListTitle = styled.span`
	font-family: Livvic;
	font-size: 15px;
	font-weight: 700;
	line-height: 22px;
	letter-spacing: 0.005em;
	text-align: left;
	color: ${props => props.theme.colors.blackVitat || props.theme.colors["text-secondary"]};
`;

const EmptyMessageWrapper = styled.span`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100%;
	color: #6f7590;
	font-family: Livvic;
	font-size: 14px;
	font-weight: 400;
	line-height: 20px;
	letter-spacing: 0em;
	text-align: center;
`;

const ScheduleType = styled.span`
	width: 100%;
	box-sizing: border-box;
	text-align: center;
	padding: 8px;
	color: ${props => props.theme.colors.blackVitat || props.theme.colors["text-secondary"]};
	font-family: Livvic;
	font-size: 14px;
	font-weight: 700;
	line-height: 22px;
	letter-spacing: 0.005em;
	margin-top: 16px;
`;

/**
 * ### Tela de consultas do usuário
 * Mostra os atendimentos ja agendados pelo usuário e da a opção de busca por outros médicos
 */
const ScheduleScreen: FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const searchInput = useRef(null);
	const [mySchedules, setMySchedules] = useState<IMyScheduleList[]>([]);
	const [hasNextPage, setHasNextPage] = useState<boolean>(true);
	const [isNextPageLoading, setIsNextPageLoading] = useState<boolean>(false);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [selectedSchedule, setSelectedSchedule] = useState<IMySchedule>();

	// Filter Modal
	const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
	const [modalFilters, setModalFilters] = useState<IScheduleFilters>({} as IScheduleFilters);

	const loadNextPage = () => {
		if (!isNextPageLoading) {
			setIsNextPageLoading(true);
			getMySchedules(modalFilters).then(data => {
				setIsNextPageLoading(false);
				setHasNextPage(false);
				setMySchedules(prevAttendances => [...prevAttendances, ...data]);
			});
		}
	};

	useEffect(() => {
		setModalFilters({
			status: (searchParams.get("status") as StatusType) ?? undefined,
		});
	}, [searchParams]);

	useEffect(() => {
		if (Object.keys(modalFilters).length > 0) {
			setMySchedules([]);
			loadNextPage();
		}
	}, [modalFilters]);

	const navigateToSearch = (search: string, focus = false) => {
		const jsonParams: { focus?: string; search?: string } = {};
		if (focus) jsonParams.focus = "true";
		jsonParams.search = search;

		navigate({
			pathname: "/medicos",
			search: createSearchParams(jsonParams).toString(),
		});
	};

	const handleCancelClick = (schedule: IMySchedule) => {
		setIsModalOpen(true);
		setSelectedSchedule(schedule);
	};

	const handleClose = () => {
		setIsModalOpen(false);
		setSelectedSchedule(undefined);
		setMySchedules([]);
		loadNextPage();
	};

	const handleApplyScheduleFilter = (filters: IScheduleFilters) => {
		setModalFilters(prevFilters => ({
			...prevFilters,
			...filters,
		}));

		const params: Record<string, string> = {};
		if (filters.status) params.status = filters.status;

		const searchParams = createSearchParams(params).toString();

		navigate(
			{
				pathname: "/consultas",
				search: searchParams,
			},
			{
				replace: true,
			},
		);
	};

	return (
		<PageWrapper>
			<Wrapper>
				<Title>Novos agendamentos</Title>
				<ContentArea>
					<SearchField
						placeholder="Pesquise aqui"
						onChange={event => {
							navigateToSearch(event.target.value, true);
						}}
						inputRef={searchInput}
					/>
				</ContentArea>
				<SubTitle>Especialidade</SubTitle>

				<ContentArea>
					<QuickSearch />
				</ContentArea>

				<FilterWrapper>
					{objectHaveOnlyUndefinedProperties(modalFilters) ? (
						<ListTitle>Meus Agendamentos</ListTitle>
					) : (
						<ScheduleFilters filters={modalFilters} handleChangeFilters={handleApplyScheduleFilter} />
					)}
					<ButtonSecondary icon={<FilterIcon color="#002B49" size={14} />} label="Filtrar" onClick={() => setIsFilterModalOpen(true)} />
				</FilterWrapper>

				<MySchedulesWrapper>
					{isNextPageLoading ? (
						<>
							<CardAttendanceLoader />
							<CardAttendanceLoader />
						</>
					) : (
						<>
							{mySchedules.length === 0 ? (
								<EmptyMessageWrapper>
									{modalFilters.status
										? "Não foi encontrado nenhum agendamento para o filtro selecionado"
										: "Você ainda não possui nenhum agendamento."}
								</EmptyMessageWrapper>
							) : (
								<LazyLoad
									SkeletonComponent={CardAttendanceLoader}
									hasNextPage={hasNextPage}
									loadNextPage={loadNextPage}
									skeletonNumber={2}
									customStyles={{ alignItems: "center", width: "100%" }}
									direction={"column"}
								>
									{mySchedules.map(schedules => (
										<Fragment key={schedules.tipo}>
											<ScheduleType>{schedules.agendamentos.length > 1 ? `${schedules.tipo}s` : schedules.tipo}</ScheduleType>
											{schedules.agendamentos.map(schedule => (
												<Fragment key={schedule.id}>
													{schedule.status && <CardMySchedules schedule={schedule} handleCancel={() => handleCancelClick(schedule)} />}
												</Fragment>
											))}
										</Fragment>
									))}
								</LazyLoad>
							)}
						</>
					)}
				</MySchedulesWrapper>
				<FilterMySchedulesModal
					isModalOpen={isFilterModalOpen}
					handleClose={() => setIsFilterModalOpen(false)}
					handleApply={handleApplyScheduleFilter}
				/>
				{selectedSchedule && (
					<CancelSchedulingModal
						isOpen={isModalOpen}
						handleClose={handleClose}
						schedulingId={selectedSchedule.id}
						professionalId={selectedSchedule.profissional}
						scheduleProcedureId={selectedSchedule.idProcedimento}
					/>
				)}
			</Wrapper>
		</PageWrapper>
	);
};

export default ScheduleScreen;
